import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import { messageFromError } from "../../../shared/api/drfErrors";
import { selectIsAuthed } from "../../../selectors/userSelectors";

import {
  cancelSubscription,
  fetchMyPayments,
  fetchMySubscription,
  fetchPlans,
  initiateCheckout,
} from "../api/payment";
import {
  billingIntervalLabel,
  formatMoney,
  formatPeriodEnd,
  paymentStatusClass,
  paymentStatusLabel,
  subscriptionStatusLabel,
} from "../lib/billingFormat";

function SubscriptionStatusBanner({ subscription }) {
  if (!subscription) return null;

  const { status, cancel_at_period_end, is_in_grace_period, current_period_end, grace_period_end } =
    subscription;

  let tone = "info";
  let message = `Статус: ${subscriptionStatusLabel(status)}. Діє до ${formatPeriodEnd(current_period_end)}.`;

  if (status === "trial") {
    message = `Пробний період до ${formatPeriodEnd(current_period_end)}.`;
  } else if (status === "past_due" && is_in_grace_period) {
    tone = "warning";
    message = `Підписка прострочена. Період пільги до ${formatPeriodEnd(grace_period_end)} — оновіть оплату.`;
  } else if (status === "expired") {
    tone = "danger";
    message = "Підписка закінчилась. Оберіть план нижче, щоб поновити доступ.";
  } else if (cancel_at_period_end) {
    message = `Скасування заплановано. Доступ до ${formatPeriodEnd(current_period_end)}.`;
  }

  return (
    <div
      className={`pay-status pay-status--${tone === "warning" ? "pending" : tone === "danger" ? "failed" : "paid"}`}
      data-testid="billing-status-banner"
      role="status"
    >
      <i className="fa-solid fa-circle-info" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}

function PlanCard({ plan, onCheckout, checkoutPlanId }) {
  const isLoading = checkoutPlanId === plan.id;
  const interval = billingIntervalLabel(plan.billing_interval);

  return (
    <article className="sub-card" data-testid={`billing-plan-${plan.id}`}>
      <div className="sub-card__head">
        <span className="sub-card__badge">
          <i className="fa-solid fa-star" aria-hidden="true" />
          Pro
        </span>
        <h2 className="sub-card__title">{plan.name}</h2>
        <p className="sub-card__price">
          <span className="sub-card__amount">{Number(plan.price_amount).toLocaleString("uk-UA")}</span>
          <span className="sub-card__currency"> {plan.price_currency}</span>
          <span className="sub-card__period"> / {interval}</span>
        </p>
        {plan.description ? <p className="sub-card__desc">{plan.description}</p> : null}
      </div>
      <div className="sub-card__pay">
        <button
          type="button"
          className="action-btn action-btn--primary"
          onClick={() => onCheckout(plan.id)}
          disabled={Boolean(checkoutPlanId)}
          data-testid={`billing-checkout-${plan.id}`}
        >
          {isLoading ? "Перенаправлення…" : "Оплатити через Monobank"}
        </button>
        <p className="sub-card__note">
          <i className="fa-solid fa-lock" aria-hidden="true" />
          Безпечна оплата на стороні Monobank
        </p>
      </div>
    </article>
  );
}

function PaymentHistoryTable({ payments }) {
  if (!payments.length) {
    return (
      <p className="pay-history__sub" data-testid="billing-history-empty">
        Платежів ще немає.
      </p>
    );
  }

  return (
    <div className="pay-history__table-wrap">
      <table className="pay-history__table" data-testid="billing-history-table">
        <thead>
          <tr>
            <th>Дата</th>
            <th>Сума</th>
            <th>Статус</th>
          </tr>
        </thead>
        <tbody>
          {payments.map((payment) => (
            <tr key={payment.id} data-testid={`billing-payment-row-${payment.id}`}>
              <td data-label="Дата">{formatPeriodEnd(payment.created_at)}</td>
              <td data-label="Сума">{formatMoney(payment.amount, payment.currency)}</td>
              <td data-label="Статус">
                <span className={`pay-status ${paymentStatusClass(payment.status)}`}>
                  {paymentStatusLabel(payment.status)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BillingScreenContent() {
  const isAuthed = useSelector(selectIsAuthed);
  const navigate = useNavigate();
  const [loadState, setLoadState] = useState("idle");
  const [loadError, setLoadError] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [payments, setPayments] = useState([]);
  const [checkoutPlanId, setCheckoutPlanId] = useState(null);
  const [cancelState, setCancelState] = useState("idle");
  const [cancelMessage, setCancelMessage] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const requestIdRef = useRef(0);

  const loadBilling = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoadState("loading");
    setLoadError(null);

    try {
      const [plansData, paymentsData] = await Promise.all([
        fetchPlans(),
        fetchMyPayments(),
      ]);
      let subscriptionData = null;
      try {
        subscriptionData = await fetchMySubscription();
      } catch (err) {
        if (err?.response?.status !== 404) {
          throw err;
        }
      }
      if (requestIdRef.current !== requestId) return;
      setPlans(Array.isArray(plansData) ? plansData : []);
      setPayments(Array.isArray(paymentsData) ? paymentsData : []);
      setSubscription(subscriptionData);
      setLoadState("loaded");
    } catch (err) {
      if (requestIdRef.current !== requestId) return;
      setLoadError(messageFromError(err));
      setLoadState("error");
    }
  }, []);

  useEffect(() => {
    if (!isAuthed) {
      navigate("/login", { replace: true });
      return undefined;
    }
    void loadBilling();
    return undefined;
  }, [isAuthed, navigate, loadBilling]);

  const handleCheckout = async (planId) => {
    setCheckoutPlanId(planId);
    setLoadError(null);
    try {
      const result = await initiateCheckout(planId);
      if (result?.checkout_url) {
        window.location.href = result.checkout_url;
        return;
      }
      setLoadError("Не вдалося отримати посилання на оплату.");
    } catch (err) {
      setLoadError(messageFromError(err));
    } finally {
      setCheckoutPlanId(null);
    }
  };

  const handleCancelRequest = () => {
    if (cancelState === "loading") return;
    setCancelConfirmOpen(true);
  };

  const handleCancelDismiss = () => {
    if (cancelState === "loading") return;
    setCancelConfirmOpen(false);
  };

  const handleCancelConfirm = async () => {
    if (cancelState === "loading") return;
    setCancelConfirmOpen(false);
    setCancelState("loading");
    setCancelMessage(null);
    try {
      const updated = await cancelSubscription();
      setSubscription(updated);
      setCancelMessage("Підписку скасовано. Доступ діє до кінця періоду.");
      setCancelState("done");
    } catch (err) {
      setCancelMessage(messageFromError(err));
      setCancelState("error");
    }
  };

  if (!isAuthed) return null;

  if (loadState === "loading" || loadState === "idle") {
    return (
      <div className="pay-shell" data-testid="billing-loading">
        <Spinner size="lg" label="Завантаження оплати" />
      </div>
    );
  }

  if (loadState === "error") {
    return (
      <div className="pay-shell">
        <EmptyState
          icon="fa-solid fa-triangle-exclamation"
          title="Не вдалося завантажити дані оплати"
          description={loadError || "Спробуйте оновити сторінку."}
          primaryAction={(
            <button type="button" className="action-btn" onClick={() => void loadBilling()}>
              Спробувати знову
            </button>
          )}
        />
      </div>
    );
  }

  const canCancel =
    subscription
    && !subscription.cancel_at_period_end
    && ["active", "trial", "past_due"].includes(subscription.status);

  return (
    <div className="pay-shell" data-testid="billing-screen">
      <header className="pay-history__head">
        <h1 className="pay-history__title">Оплата та підписка</h1>
        <p className="pay-history__sub">
          Керуйте підпискою Pro, переглядайте історію платежів і поновлюйте доступ.
        </p>
      </header>

      {loadError ? (
        <p className="feature-notifications__body" role="alert">{loadError}</p>
      ) : null}

      <SubscriptionStatusBanner subscription={subscription} />

      {cancelMessage ? (
        <p data-testid="billing-cancel-feedback" role="status">{cancelMessage}</p>
      ) : null}

      {canCancel ? (
        <div>
          <button
            type="button"
            className="action-btn"
            onClick={handleCancelRequest}
            disabled={cancelState === "loading"}
            data-testid="billing-cancel-action"
            aria-expanded={cancelConfirmOpen}
          >
            <i className="fa-solid fa-calendar-xmark" aria-hidden="true" />
            <span>{cancelState === "loading" ? "Скасування…" : "Скасувати наприкінці періоду"}</span>
          </button>
          {cancelConfirmOpen ? (
            <div
              className="pay-cancel-confirm"
              role="alertdialog"
              aria-labelledby="billing-cancel-confirm-title"
              aria-describedby="billing-cancel-confirm-desc"
              data-testid="billing-cancel-confirm"
            >
              <p id="billing-cancel-confirm-title" className="pay-cancel-confirm__title">
                Скасувати підписку?
              </p>
              <p id="billing-cancel-confirm-desc" className="pay-cancel-confirm__desc">
                Скасувати підписку наприкінці поточного періоду? Доступ
                збережеться до дати закінчення.
              </p>
              <div className="pay-cancel-confirm__actions">
                <button
                  type="button"
                  className="action-btn"
                  onClick={handleCancelDismiss}
                  disabled={cancelState === "loading"}
                >
                  Скасувати
                </button>
                <button
                  type="button"
                  className="action-btn action-btn--danger"
                  onClick={() => void handleCancelConfirm()}
                  disabled={cancelState === "loading"}
                  data-testid="billing-cancel-confirm-action"
                >
                  {cancelState === "loading"
                    ? "Скасування…"
                    : "Підтвердити скасування"}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <section aria-label="Каталог планів" data-testid="billing-plan-catalog">
        <div style={{ display: "grid", gap: "var(--space-6)", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              onCheckout={handleCheckout}
              checkoutPlanId={checkoutPlanId}
            />
          ))}
        </div>
      </section>

      <section className="pay-history" aria-label="Історія платежів">
        <div className="pay-history__head">
          <h2 className="pay-history__title">Історія платежів</h2>
        </div>
        <PaymentHistoryTable payments={payments} />
      </section>
    </div>
  );
}

export default BillingScreenContent;
